import * as Sentry from '@sentry/react';
import { serializeForSentry, serializeRecordForSentry } from '@/utils/sentry';

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
};

const getConsoleArgumentsFromHint = (hint: unknown): unknown[] => {
  if (!isRecord(hint)) {
    return [];
  }

  const input = hint.input;
  if (!Array.isArray(input)) {
    return [];
  }

  return input;
};

export const initSentry = () => {
  const environment = import.meta.env.MODE;
  const isDevelopment = environment !== 'production';

  // Skip Sentry initialization in development mode
  if (isDevelopment) {
    console.info('Sentry disabled in development mode.');
    return;
  }

  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn('Sentry DSN not found. Sentry will not be initialized.');
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring - production values only
    tracesSampleRate: 0.1, // 10% in production
    // Set sample rate for profiling
    profilesSampleRate: 0.1, // 10% in production
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% in production
    replaysOnErrorSampleRate: 1.0, // Always capture replay on errors
    environment,

    // Filter out common noise in production
    beforeSend(event) {
      const errorMessage = event.exception?.values?.[0]?.value;
      if (errorMessage) {
        // Filter out network errors that might be temporary
        if (
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('fetch')
        ) {
          return null;
        }
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category !== 'console') {
        return breadcrumb;
      }

      const consoleArguments = getConsoleArgumentsFromHint(hint);

      if (consoleArguments.length === 0) {
        return breadcrumb;
      }

      const serializedArguments = consoleArguments.map((argument) =>
        serializeForSentry(argument)
      );

      return {
        ...breadcrumb,
        data: serializeRecordForSentry({
          ...(breadcrumb.data ?? {}),
          consoleArguments: serializedArguments,
        }),
      };
    },

    // Set initial tags
    initialScope: {
      tags: {
        component: 'frontend',
        version: import.meta.env.VITE_APP_VERSION || 'unknown',
      },
    },
  });
};
